<!-- src/components/PopupWindow.vue -->
<template>
  <div
    class="popup"
    :style="styleObject"
    role="dialog"
    aria-modal="true"
    :aria-label="popup.altText || '프로모션 팝업'"
    @mousedown="bringToFront"
    @touchstart.passive="bringToFront"
  >
    <!-- 드래그 핸들(헤더바) -->
    <div
      class="popup__header"
      @pointerdown="onPointerDown"
    >
      <strong class="popup__title">알림</strong>
      <button class="popup__close" type="button" @click.stop="close" aria-label="닫기">×</button>
    </div>

    <div class="popup__body">
      <a
        v-if="popup.link"
        :href="popup.link"
        :target="popup.linkTarget || '_blank'"
        :rel="(popup.linkTarget || '_blank') === '_blank' ? 'noopener noreferrer' : undefined"
        class="popup__imageLink"
        @click="onFollowLink"
      >
        <img :src="src" :alt="popup.altText || 'popup'" />
      </a>
      <img v-else :src="src" :alt="popup.altText || 'popup'" />
    </div>

    <div class="popup__footer">
      <label class="popup__suppress">
        <input type="checkbox" v-model="doNotShowToday" @change="onToggleDoNotShowToday"/>
        오늘 하루 보지 않기
      </label>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watchEffect } from 'vue'

const props = defineProps({
  popup: { type: Object, required: true },
  apiBaseUrl: { type: String, default: '' },
  initialX: { type: Number, default: 0 },
  initialY: { type: Number, default: 0 },
  zIndex: { type: Number, default: 10000 },
})

const emit = defineEmits(['close', 'focus'])

const doNotShowToday = ref(false)

const x = ref(props.initialX)
const y = ref(props.initialY)

const dragging = ref(false)
const start = ref({ px: 0, py: 0, ox: 0, oy: 0 })

const src = computed(() => {
  // popup.popupUrl이 절대경로/상대경로 모두 올 수 있으니 방어
  if (!props.popup?.popupUrl) return ''
  if (/^https?:\/\//i.test(props.popup.popupUrl)) return props.popup.popupUrl
  return `${props.apiBaseUrl}${props.popup.popupUrl}`
})

const styleObject = computed(() => ({
  transform: `translate3d(${x.value}px, ${y.value}px, 0)`,
  zIndex: props.zIndex,
}))

function setSkipIfChecked() {
  if (!doNotShowToday.value) return
  const now = new Date()
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0)
  localStorage.setItem(props.popup.storageKey, String(midnight.getTime()))
}

function close() {
  setSkipIfChecked()
  emit('close', props.popup)
}

function onFollowLink() {
  // 링크 눌러도 팝업 닫히게(원하시면 유지도 가능)
  setSkipIfChecked()
  emit('close', props.popup)
}

function bringToFront() {
  emit('focus', props.popup)
}

function onToggleDoNotShowToday() {
  if (doNotShowToday.value) {
    close() // setSkipIfChecked() + emit('close', popup)까지 처리됨
  }
}


function clampToViewport() {
  // 화면 밖으로 너무 나가지 않게 최소한만 클램프
  // (정밀한 bounding은 DOM 측정이 필요하나, 여기서는 과도한 복잡도 없이 처리)
  const maxX = Math.max(0, window.innerWidth - 320)
  const maxY = Math.max(0, window.innerHeight - 240)
  x.value = Math.min(Math.max(0, x.value), maxX)
  y.value = Math.min(Math.max(0, y.value), maxY)
}

// function onPointerDown(e) {
//   dragging.value = true
//   start.value = { px: e.clientX, py: e.clientY, ox: x.value, oy: y.value }
//   e.currentTarget.setPointerCapture(e.pointerId)
//   window.addEventListener('pointermove', onPointerMove, { passive: false })
//   window.addEventListener('pointerup', onPointerUp, { passive: true })
// }

function onPointerDown(e) {
  if (e.target?.closest?.('button, a, input, label')) return

  dragging.value = true
  start.value = { px: e.clientX, py: e.clientY, ox: x.value, oy: y.value }
  e.currentTarget.setPointerCapture(e.pointerId)
  window.addEventListener('pointermove', onPointerMove, { passive: false })
  window.addEventListener('pointerup', onPointerUp, { passive: true })
}


function onPointerMove(e) {
  if (!dragging.value) return
  e.preventDefault()
  x.value = start.value.ox + (e.clientX - start.value.px)
  y.value = start.value.oy + (e.clientY - start.value.py)
}

function onPointerUp() {
  dragging.value = false
  clampToViewport()
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
}

onBeforeUnmount(() => {
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
})

watchEffect(() => {
  // 리사이즈 시 현재 위치가 화면 밖이면 보정
  const onResize = () => clampToViewport()
  window.addEventListener('resize', onResize, { passive: true })
  onBeforeUnmount(() => window.removeEventListener('resize', onResize))
})
</script>

<style scoped>
.popup {
  position: fixed;
  top: 0;
  left: 0;
  width: min(500px, 92vw);

  max-height: 90vh;
  display: flex;
  flex-direction: column;

  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  /* box-shadow: 0 20px 60px rgba(0, 0, 0, .35);
  user-select: none; */
}

.popup__header {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 10px 10px;
  background: #f6f8fb;
  border-bottom: 1px solid #6d6d6d;
  cursor: grab;
  touch-action: none; /* 드래그 안정화 */
}
.popup__header:active { cursor: grabbing; }

.popup__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2937;
}

.popup__close {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  font-size: 22px;
  line-height: 1;
  background: rgba(255,255,255,.9);
}
.popup__close:hover { background: #fff; }

.popup__body img { 
  display: block; 
  width: 100%; 
  max-height: 600px;
  /* ★ 핵심 */ 
  /* object-fit: contain; */
  /* 이미지 잘림 없이 축소 */ }
.popup__imageLink { display: block; }
.popup__imageLink:active { cursor: grabbing; }


.popup__footer {
  display: flex;
  justify-content: flex-end;
  padding: 10px 12px 12px;
  border-top: 1px solid #6d6d6d;
}
.popup__suppress {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #334155;
}
</style>
